import {
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  CircleAlert,
  Info,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type {
  WhatsAppNumberActivation,
  WhatsAppPhoneNumberEntry,
} from '@/Domain/api/WhatsAppPhoneNumbers/WhatsAppPhoneNumbers.api';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Badge, type BadgeVariantProps } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { cn } from '@/Domain/utils/cn';

export type DisplayNameState = 'pending' | 'rejected' | 'missing';

// Meta's `name_status` values that keep a number from sending replies; anything else is fine or unknown.
const DISPLAY_NAME_STATE: Record<string, DisplayNameState> = {
  PENDING_REVIEW: 'pending',
  DECLINED: 'rejected',
  EXPIRED: 'rejected',
  NONE: 'missing',
};
const DISPLAY_NAME_SEVERITY: Record<DisplayNameState, number> = {
  rejected: 3,
  missing: 2,
  pending: 1,
};
const DISPLAY_NAME_TITLE_KEY: Record<DisplayNameState, string> = {
  pending: 'whatsapp_phone_numbers.health.display_name_pending_title',
  rejected: 'whatsapp_phone_numbers.health.display_name_rejected_title',
  missing: 'whatsapp_phone_numbers.health.display_name_missing_title',
};
const DISPLAY_NAME_DESCRIPTION_KEY: Record<DisplayNameState, string> = {
  pending: 'whatsapp_phone_numbers.health.display_name_pending_description',
  rejected: 'whatsapp_phone_numbers.health.display_name_rejected_description',
  missing: 'whatsapp_phone_numbers.health.display_name_missing_description',
};
// Meta documents the display-name send block for its 555 numbers only, so a review in progress is news, not a warning.
const DISPLAY_NAME_ALERT_VARIANT: Record<DisplayNameState, 'info' | 'warning'> = {
  pending: 'info',
  rejected: 'warning',
  missing: 'warning',
};
const NAME_STATUS_BADGE_VARIANT: Record<string, NonNullable<BadgeVariantProps['variant']>> = {
  APPROVED: 'success',
  AVAILABLE_WITHOUT_REVIEW: 'success',
  PENDING_REVIEW: 'warning',
  DECLINED: 'error',
  EXPIRED: 'error',
  NONE: 'secondary',
};
const NAME_STATUS_DISPLAY_KEY: Record<string, string> = {
  APPROVED: 'whatsapp_phone_numbers.card.name_status_approved',
  AVAILABLE_WITHOUT_REVIEW: 'whatsapp_phone_numbers.card.name_status_approved',
  PENDING_REVIEW: 'whatsapp_phone_numbers.card.name_status_pending',
  DECLINED: 'whatsapp_phone_numbers.card.name_status_declined',
  EXPIRED: 'whatsapp_phone_numbers.card.name_status_expired',
  NONE: 'whatsapp_phone_numbers.card.name_status_none',
};
// The same page the setup-nudge email sends people to.
const WHATSAPP_MANAGER_HREF = 'https://business.facebook.com/latest/whatsapp_manager/';

// Keeps a phone number on one line when the sentence around it wraps.
const unbreakable = (number: string): string =>
  number.replace(/ /g, '\u00a0').replace(/-/g, '\u2011');

export interface ConnectedHealth {
  displayNameState: DisplayNameState | null;
  // The number whose display name produced the state, so the remediation names the right one.
  displayNameNumber: string | null;
  isTestNumber: boolean;
  firstActiveNumber: string | null;
}

export const deriveConnectedHealth = (
  phoneNumbers: WhatsAppPhoneNumberEntry[],
): ConnectedHealth => {
  const active = phoneNumbers.filter((entry) => entry.is_active);
  let displayNameState: DisplayNameState | null = null;
  let displayNameNumber: string | null = null;
  for (const entry of active) {
    const state = entry.name_status ? DISPLAY_NAME_STATE[entry.name_status] : undefined;
    if (
      state &&
      (displayNameState === null ||
        DISPLAY_NAME_SEVERITY[state] > DISPLAY_NAME_SEVERITY[displayNameState])
    ) {
      displayNameState = state;
      displayNameNumber = entry.display_phone_number;
    }
  }
  return {
    displayNameState,
    displayNameNumber,
    isTestNumber: active.some((entry) => entry.is_test_number === true),
    firstActiveNumber: active[0]?.display_phone_number ?? null,
  };
};

export const NameStatusBadge = ({
  entry,
  className,
}: {
  entry: WhatsAppPhoneNumberEntry;
  className?: string;
}) => {
  const { t } = useTranslation(['integrations']);
  const status = entry.name_status ?? '';
  const displayKey = NAME_STATUS_DISPLAY_KEY[status];
  if (!displayKey) return null;
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('inline-flex', className)}>
            <Badge
              variant={NAME_STATUS_BADGE_VARIANT[status] ?? 'secondary'}
              className="text-xs whitespace-nowrap cursor-default"
              data-testid={`name-status-${entry.phone_number_id}`}
            >
              {t(displayKey)}
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent>{t('whatsapp_phone_numbers.card.name_status_tooltip')}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const ManagerLink = ({ label, testId }: { label: string; testId: string }) => (
  <a
    href={WHATSAPP_MANAGER_HREF}
    target="_blank"
    rel="noopener noreferrer"
    className="mt-2 inline-flex items-center gap-1 text-xs font-medium underline"
    data-testid={testId}
  >
    {label}
    <ArrowUpRight className="size-3" />
  </a>
);

interface ConnectedHealthPanelProps {
  phoneNumbers: WhatsAppPhoneNumberEntry[];
  activationNumbers?: WhatsAppNumberActivation[];
  // Meta has already refused a reply over the display name, so the alert is an error, not a caution.
  displayNameRepliesFailing: boolean;
  onRefresh: () => void;
  refreshing: boolean;
}

export const ConnectedHealthPanel = ({
  phoneNumbers,
  activationNumbers,
  displayNameRepliesFailing,
  onRefresh,
  refreshing,
}: ConnectedHealthPanelProps) => {
  const { t } = useTranslation(['integrations']);
  const health = deriveConnectedHealth(phoneNumbers);

  // With nothing active there is no destination to message: activation comes first.
  if (health.firstActiveNumber === null) {
    return (
      <div className="flex flex-col gap-3" data-testid="connected-health-panel">
        <Alert variant="warning" data-testid="connected-health-no-active-number">
          <AlertTriangle className="size-4" />
          <AlertTitle className="text-balance leading-tight">
            {t('whatsapp_phone_numbers.health.no_active_number_title')}
          </AlertTitle>
          <AlertDescription>
            <p className="text-pretty">
              {t('whatsapp_phone_numbers.health.no_active_number_description')}
            </p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  const number = health.firstActiveNumber;
  const verificationNumbers =
    activationNumbers ??
    phoneNumbers
      .filter((entry) => entry.is_active)
      .map(
        (entry): WhatsAppNumberActivation => ({
          phone_number_id: entry.phone_number_id,
          display_phone_number: entry.display_phone_number,
          status: entry.is_test_number ? 'test_number' : 'awaiting_inbound',
        }),
      );

  return (
    <div className="flex flex-col gap-3" data-testid="connected-health-panel">
      {health.displayNameState ? (
        <Alert
          variant={
            displayNameRepliesFailing
              ? 'destructive'
              : DISPLAY_NAME_ALERT_VARIANT[health.displayNameState]
          }
          data-testid="connected-health-display-name"
          data-state={health.displayNameState}
          data-replies-failing={displayNameRepliesFailing}
        >
          {displayNameRepliesFailing ? (
            <CircleAlert className="size-4" />
          ) : health.displayNameState === 'pending' ? (
            <Info className="size-4" />
          ) : (
            <AlertTriangle className="size-4" />
          )}
          <AlertTitle className="text-balance leading-tight">
            {t(DISPLAY_NAME_TITLE_KEY[health.displayNameState])}
          </AlertTitle>
          <AlertDescription>
            <p className="text-pretty">
              {t(DISPLAY_NAME_DESCRIPTION_KEY[health.displayNameState], {
                number: unbreakable(health.displayNameNumber ?? number),
              })}
            </p>
            {displayNameRepliesFailing ? (
              <p className="text-pretty">
                {t('whatsapp_phone_numbers.health.display_name_replies_failing', {
                  number: unbreakable(health.displayNameNumber ?? number),
                })}
              </p>
            ) : null}
            <ManagerLink
              label={t('whatsapp_phone_numbers.health.open_whatsapp_manager')}
              testId="connected-health-display-name-link"
            />
          </AlertDescription>
        </Alert>
      ) : null}

      {health.isTestNumber ? (
        <Alert variant="info" data-testid="connected-health-test-number">
          <Info className="size-4" />
          <AlertTitle className="text-balance leading-tight">
            {t('whatsapp_phone_numbers.health.test_number_title')}
          </AlertTitle>
          <AlertDescription>
            <p className="text-pretty">
              {t('whatsapp_phone_numbers.health.test_number_description')}
            </p>
            <ManagerLink
              label={t('whatsapp_phone_numbers.health.open_whatsapp_manager')}
              testId="connected-health-test-number-link"
            />
          </AlertDescription>
        </Alert>
      ) : null}

      {verificationNumbers
        .filter(
          (entry) =>
            entry.status !== 'test_number' &&
            !phoneNumbers.find((phone) => phone.phone_number_id === entry.phone_number_id)
              ?.is_test_number,
        )
        .map((entry) => {
          const verified = entry.status === 'verified';
          // The saved row can predate its display number; Meta's live list still names it.
          const displayNumber =
            entry.display_phone_number ||
            phoneNumbers.find((phone) => phone.phone_number_id === entry.phone_number_id)
              ?.display_phone_number ||
            '';
          const key = verified
            ? 'reply_verified'
            : entry.status === 'awaiting_reply'
              ? 'reply_waiting'
              : entry.status === 'blocked'
                ? 'verification_blocked'
                : 'first_message_waiting';
          return (
            <Alert
              key={entry.phone_number_id}
              variant={verified ? 'default' : entry.status === 'blocked' ? 'warning' : 'info'}
              className={verified ? 'border-success-muted bg-success-muted' : undefined}
              data-testid={`whatsapp-verification-${entry.phone_number_id}`}
              data-status={entry.status}
            >
              {verified ? <CheckCircle2 className="size-4" /> : <Info className="size-4" />}
              <AlertTitle>{t(`whatsapp_phone_numbers.health.${key}_title`)}</AlertTitle>
              <AlertDescription>
                <p>
                  {t(`whatsapp_phone_numbers.health.${key}_description`, {
                    number: unbreakable(displayNumber),
                  })}
                </p>
                {!verified && (
                  <div className="pt-3 !pl-0">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={onRefresh}
                      loading={refreshing}
                      data-testid={`whatsapp-verification-refresh-${entry.phone_number_id}`}
                    >
                      <RefreshCw data-icon="inline-start" />
                      {t('whatsapp_phone_numbers.health.first_message_refresh')}
                    </Button>
                  </div>
                )}
              </AlertDescription>
            </Alert>
          );
        })}
    </div>
  );
};
